import { Col, Row, FormLabel, InputGroup, FormControl } from "react-bootstrap";
import { IFieldEditor, IMediaTreeItem } from "../../../../models/Interfaces";
import InputGroupText from "react-bootstrap/esm/InputGroupText";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage, faRefresh, faTimes } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useRef, useState } from "react";
import TreeSelect from "../../../components/common/TreeSelect";
import { MediaService } from "../../../../services/MediaService";
import Loading from "../../../components/common/Loading";
import { TreeItemSort } from "../../../../config/Enums";
import AppEmptyState from "../../../components/common/AppEmptyState";
import { MediaValueHelper } from "../../../../helpers/MediaValueHelper";


const ImageSelectEditor = (props: IFieldEditor) => {

  const [ siteMedia, setSiteMedia ] = useState<Array<IMediaTreeItem>>([]);
  const [ loading, setLoading ] = useState<boolean>(true);

  const mediaSvcRef = useRef<MediaService>();
  const selectedMediaUrl = MediaValueHelper.getUrl(props.property.PublishedValue);

  const resolveSelectedMedia = (value: string): IMediaTreeItem | null => {
    return siteMedia.find((item) => item.url === value) ?? null;
  };
  
  
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    props.onValueChange(props.property.Id, value);
  };

  const handleTreeSelect = (value: string) : void => {
    if (value) {
      const selectedMedia = resolveSelectedMedia(value);
      props.onValueChange(
        props.property.Id,
        MediaValueHelper.create(selectedMedia?.itemid ?? null, value)
      );
    }
  };

  const mediaIsVideo = () : boolean => {
    const videoMimeTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/mov', 'video/avi', 'video/mpeg'];
    const mediaUrl = selectedMediaUrl;

    if (mediaUrl) {
      const fileType = mediaUrl.toLowerCase();
    
      if (videoMimeTypes.includes(fileType)) {
        return true;
      }
    
      // Fallback to check file extension if MIME type is not available
      const extension = mediaUrl.split('.').pop()?.toLowerCase();
      const videoExtensions = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mpeg'];
    
      if (extension && videoExtensions.includes(extension)) {
        return true;
      }
    }
    return false;
  }

  const getSiteMedia = async () => {
    if (mediaSvcRef.current) {
      const res = await mediaSvcRef.current.getMediaTree();

      if (res) {
        setSiteMedia(res);
      }
    }
  }

  useEffect(() => {
    if (!mediaSvcRef.current) {
      mediaSvcRef.current = new MediaService();
    }

    const getMedia = async () => {
      try {
        await Promise.all([
          getSiteMedia()
        ]);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    }

    getMedia();
    
  }, []);

  useEffect(() => {
    console.log("Image property PublishedValue", props.property.PublishedValue);
  }, [props.property.PublishedValue]);

  return (
    <>
      { loading && (
        <Loading Message="loading site media..." SpinSize="2x" />
      )}

      { !loading && (
        <Row className="m-0 p-0" name="ImageSelectEditor">
          <Col md={12} xl={6} className="m-0 p-0">
            <FormLabel>{props.property.DisplayLabel}</FormLabel>
            <InputGroup>
              <FormControl 
                type="text"
                placeholder="enter an image url or select from uploaded media..."
                value={selectedMediaUrl}
                onChange={handleInputChange}>
              </FormControl>
              <InputGroupText >
                <div title="clear image" onClick={() => props.onValueChange(props.property.Id, "")} style={{cursor: "pointer"}}>
                  <FontAwesomeIcon icon={faTimes} />
                </div>
              </InputGroupText>
            </InputGroup>
            { siteMedia.length > 0 && (
              <InputGroup className="mt-3 w-100">
                <TreeSelect 
                  idKey="id"
                  valueKey="url" 
                  typeKey="type"
                  displayKey="text" 
                  parentKey="parent"
                  selectnodetype="file"
                  initialValue={selectedMediaUrl} 
                  items={siteMedia} 
                  onItemSelect={handleTreeSelect} 
                  openText="select site image"
                  closeText="close image list"
                  style={{ flexGrow: 1}}
                  rootNodeKeyValue={"#"}
                  closeOnSelect={false}
                  hideClear={true}
                  triggerClassName="page-selector__trigger"
                  popoverTitle="Select Media"
                  sortBy={TreeItemSort.Ascending} />
                <InputGroupText className="page-selector__action page-selector__action--last">
                  <FontAwesomeIcon icon={faRefresh} title="refresh site images" onClick={getSiteMedia} style={{cursor: "pointer"} } />
                </InputGroupText>
              </InputGroup>
            )}
          </Col>
          <Col md={12} xl={6} className="m-0 p-0 p-3 text-center" name="MediaPreview">
            {props.property.PublishedValue && (
              mediaIsVideo() ? (
                <video
                  src={selectedMediaUrl}
                  className="img-fluid mw-100"
                  style= {{ maxHeight: "190px "}}
                  controls
                ></video>
              ) : (
                <img
                  src={selectedMediaUrl}
                  className="img-fluid mw-100"
                  style= {{ maxHeight: "190px "}}
                  alt="Media"
                />
              )
            )}
            {!props.property.PublishedValue && (
              <AppEmptyState
                icon={faImage}
                title="No media selected"
                message="Choose an uploaded image or paste a media URL to preview it here."
              />
            )}
          </Col>
        </Row>
      )}
    </>
  );
}

export default ImageSelectEditor;
